import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import {
  type AlertStatus,
  AnalyticsServiceV2,
  type SuspiciousActivityAlert,
} from "@/services/features/AnalyticsServiceV2";
import { Store, StoreService } from "@/services/features/StoreService";
import { User, UserService } from "@/services/features/UserService";
import { DateRange, storePerformanceService } from "@/services/StorePerformanceService";
import { getBrowserTimezone } from "@/utils/storeTime";

interface SuspiciousActivityTag {
  id: number;
  type: string;
  label: string;
  note?: string | null;
}

export interface SuspiciousActivityOccurrence {
  id: number;
  time: string;
  createdAt: number;
  completedAt?: number;
  storeId: number;
  storeName: string;
  storeNumber: string;
  roomId: number;
  roomLabel: string;
  suspiciousActivities: SuspiciousActivityTag[];
  confirmedByName: string;
  status: AlertStatus;
  statusLabel: string;
}

const ACTIVITY_LABEL_KEYS: Record<string, string> = {
  DUPLICATE_PRODUCT_FAMILY: "storeInsights.suspiciousActivityReport.alertTypes.sameProductFamily",
  DUPLICATE_ITEM_FAMILY: "storeInsights.suspiciousActivityReport.alertTypes.sameItem",
  RESTRICTED_CATEGORY: "storeInsights.suspiciousActivityReport.alertTypes.restrictedItems",
  TOO_MANY_SKUS: "storeInsights.suspiciousActivityReport.alertTypes.unusuallyHighItemCount",
  VALUE_TOO_HIGH: "storeInsights.suspiciousActivityReport.alertTypes.valueTooHigh",
  DUPLICATE_SKUS: "storeInsights.suspiciousActivityReport.alertTypes.duplicateItemsScanned",
};

const STATUS_LABEL_KEYS: Partial<Record<AlertStatus, string>> = {
  ALL_CLEAR: "storeInsights.suspiciousActivityReport.serviceStatus.noConcern",
  MISSED: "storeInsights.suspiciousActivityReport.serviceStatus.missed",
  UNABLE_TO_ASSESS: "storeInsights.suspiciousActivityReport.serviceStatus.unableToAssess",
  CONFIRMED: "storeInsights.suspiciousActivityReport.serviceStatus.concernsObserved",
};

const humanizeEnum = (value: string): string =>
  value
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatUserName = (user?: User): string => {
  if (!user) {
    return "--";
  }

  const safeFirstName = user.firstName?.trim();
  const safeLastName = user.lastName?.trim();

  if (safeFirstName && safeLastName) {
    return `${safeFirstName} ${safeLastName.charAt(0)}.`;
  }

  return safeFirstName || safeLastName || user.username || "--";
};

const formatRoomLabel = (roomId: number, roomName?: string): string => {
  const safeName = roomName?.trim();
  if (safeName) {
    return safeName;
  }

  return `FR-${String(roomId).padStart(2, "0")}`;
};

const normalizeAlerts = (
  data: SuspiciousActivityAlert[] | SuspiciousActivityAlert | null | undefined,
): SuspiciousActivityAlert[] => {
  if (!data) {
    return [];
  }

  return Array.isArray(data) ? data : [data];
};

export const useSuspiciousActivityAlerts = (
  stores: Store[] | null,
  dateRange: DateRange,
  deviceType: DeviceType,
) => {
  const { t } = useTranslation();
  const browserTimezone = getBrowserTimezone();
  const [data, setData] = useState<SuspiciousActivityOccurrence[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completedRequestKey, setCompletedRequestKey] = useState<string | null>(null);
  const storeIdsKey = stores?.map((store) => store.id).join(",") ?? "";
  const startDate = storePerformanceService.formatDate(dateRange?.start, browserTimezone);
  const endDate = storePerformanceService.formatDate(dateRange?.end, browserTimezone);
  const hasRequestInputs = Boolean(storeIdsKey && startDate && endDate);
  const requestKey = hasRequestInputs
    ? `${storeIdsKey}:${startDate}:${endDate}:${deviceType}`
    : null;

  useEffect(() => {
    if (!stores || stores.length === 0 || !dateRange) {
      setData([]);
      setLoading(false);
      setError(null);
      setCompletedRequestKey(null);
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const analyticsService = AnalyticsServiceV2.getInstance();
        const storeService = StoreService.getInstance();
        const userService = UserService.getInstance();
        const storeMap = new Map<number, Store>(stores.map((store) => [store.id, store]));
        const startDate = storePerformanceService.formatDate(dateRange.start, browserTimezone);
        const endDate = storePerformanceService.formatDate(dateRange.end, browserTimezone);
        const storeIds = stores.map((store) => store.id);
        const alertsResponse = await analyticsService.getSuspiciousActivityAlerts(
          storeIds,
          startDate,
          endDate,
          deviceType !== "all" ? deviceType : undefined,
        );
        const alertsByStoreId = new Map<number, SuspiciousActivityAlert[]>();

        normalizeAlerts(alertsResponse.data).forEach((alert) => {
          const currentAlerts = alertsByStoreId.get(alert.storeId) ?? [];
          currentAlerts.push(alert);
          alertsByStoreId.set(alert.storeId, currentAlerts);
        });

        const nextData: SuspiciousActivityOccurrence[] = [];
        const storesWithAlerts = stores.filter((store) => alertsByStoreId.has(store.id));

        for (const store of storesWithAlerts) {
          const changingRoomsResponse = await storeService.getChangingRoomsByStore(store.id);
          const usersResponse = await userService
            .getUsers({ storeId: store.id })
            .catch(() => ({ data: [] as User[] }));

          const roomMap = new Map<number, string>();
          if (changingRoomsResponse.success && changingRoomsResponse.data) {
            changingRoomsResponse.data.forEach((group) => {
              group.changingRooms.forEach((room) => {
                if (room.id !== undefined) {
                  roomMap.set(room.id, room.name);
                }
              });
            });
          }

          const userMap = new Map<number, User>(
            (usersResponse.data || []).map((user) => [user.id, user]),
          );
          const alertsForStore = alertsByStoreId.get(store.id) ?? [];
          const storeRecord = storeMap.get(store.id);

          alertsForStore.forEach((alert) => {
            const createdDate = new Date(alert.createdAt);
            if (Number.isNaN(createdDate.getTime())) {
              return;
            }

            const createdAt = createdDate.getTime();

            const completedDate = alert.completedAt ? new Date(alert.completedAt) : null;
            const completedAt =
              completedDate && !Number.isNaN(completedDate.getTime())
                ? completedDate.getTime()
                : undefined;

            nextData.push({
              id: alert.id,
              time: createdDate.toISOString(),
              createdAt,
              completedAt,
              storeId: store.id,
              storeName: storeRecord?.name || "Unknown Store",
              storeNumber: storeRecord?.id.toString() || "",
              roomId: alert.changingRoomId,
              roomLabel: formatRoomLabel(alert.changingRoomId, roomMap.get(alert.changingRoomId)),
              suspiciousActivities: (alert.suspiciousActivities || []).map((activity) => ({
                id: activity.id,
                type: activity.type,
                label: activity.type,
                note: activity.note,
              })),
              confirmedByName: alert.assignedUserId
                ? formatUserName(userMap.get(alert.assignedUserId))
                : "--",
              status: alert.status,
              statusLabel: alert.status,
            });
          });
        }

        nextData.sort((a, b) => b.createdAt - a.createdAt);

        if (!cancelled) {
          setData(nextData);
        }
      } catch (fetchError) {
        if (!cancelled) {
          console.error("Error fetching suspicious activity alerts:", fetchError);
          setError("Failed to load suspicious activity alerts");
          setData([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setCompletedRequestKey(requestKey);
        }
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [stores, dateRange, deviceType, browserTimezone, requestKey]);

  const isRequestPending = hasRequestInputs && completedRequestKey !== requestKey;

  const localizedData = useMemo(
    () =>
      data.map((alert) => ({
        ...alert,
        suspiciousActivities: alert.suspiciousActivities.map((activity) => ({
          ...activity,
          label: ACTIVITY_LABEL_KEYS[activity.type]
            ? t(ACTIVITY_LABEL_KEYS[activity.type])
            : humanizeEnum(activity.type),
        })),
        statusLabel: STATUS_LABEL_KEYS[alert.status]
          ? t(STATUS_LABEL_KEYS[alert.status] as string)
          : humanizeEnum(alert.status),
      })),
    [data, t],
  );

  return {
    data: localizedData,
    loading: loading || isRequestPending,
    error,
  };
};
